import React, { createContext, useState, useEffect, useContext } from "react";
import { authAPI } from "../services/api";
import { User, AuthContextType } from "../types/auth";

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook để dùng AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await authAPI.getMe();
          const userRole = response.data.user.role;
          if (!["roommaster", "kiemkho", "admin", "superadmin"].includes(userRole)) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            window.location.href = process.env.REACT_APP_PUBLIC_URL || "https://www.23homestay.vn/";
            return;
          }
          setUser(response.data.user);
          setIsAuthenticated(true);
        } catch (error: any) {
          // Chỉ xoá phiên khi máy chủ báo token sai/hết hạn; mất mạng hay lỗi 5xx thì giữ token để tải lại là vào được
          const status = error?.response?.status;
          if (status === 401 || status === 403 || status === 404) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await authAPI.login({ email, password });
      const { token, user } = response.data;

      // Only roommaster/admin/superadmin can login to admin panel
      if (!["roommaster", "kiemkho", "admin", "superadmin"].includes(user.role)) {
        window.location.href = process.env.REACT_APP_PUBLIC_URL || "https://www.23homestay.vn/";
        return {
          success: false,
          message: "Bạn không có quyền truy cập trang quản trị",
        };
      }

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      setUser(user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error: any) {
      const is429 = error.response?.status === 429;
      return {
        success: false,
        message: is429
          ? "Bạn đã thử đăng nhập quá nhiều lần. Vui lòng đợi 15 phút rồi thử lại."
          : error.response?.data?.message || "Đăng nhập thất bại",
      };
    }
  };

  const logout = (): void => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, loading, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};
